import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { EstadoTrabajo } from '@laser/core';
import { ETIQUETA_ESTADO } from '../lib/estados';
import { centavosDesdeTexto, textoDesdeCentavos } from '../lib/formato';

export function Chip({ estado }: { estado: EstadoTrabajo }) {
  return <span className={`chip chip--${estado}`}>{ETIQUETA_ESTADO[estado] ?? estado}</span>;
}

export type Tono = 'bad' | 'warn' | 'good' | 'accent' | 'brand' | 'neutral';
export function Insignia({ tono = 'neutral', children, title }: { tono?: Tono; children: ReactNode; title?: string }) {
  return <span className={`ins ins--${tono}`} title={title}>{children}</span>;
}

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return <div className="cargando" role="status">{texto}</div>;
}

export function ErrorCaja({ error, reintentar }: { error: unknown; reintentar?: () => void }) {
  const msg = error instanceof Error ? error.message : String(error ?? 'Error desconocido');
  return (
    <div className="aviso aviso--bad fila fila--entre" role="alert">
      <span>{msg}</span>
      {reintentar && <button className="btn btn--sm" onClick={reintentar}>Reintentar</button>}
    </div>
  );
}

export function Campo({ etiqueta, children, ayuda, ancho, htmlFor }: { etiqueta: string; children: ReactNode; ayuda?: ReactNode; ancho?: boolean; htmlFor?: string }) {
  return (
    <div className={`campo${ancho ? ' ancho' : ''}`}>
      <label htmlFor={htmlFor}>{etiqueta}</label>
      {children}
      {ayuda && <span className="ayuda">{ayuda}</span>}
    </div>
  );
}

/** Input en pesos que guarda centavos. Vacío = null. */
export function EntradaPesos({ valor, onCambio, id, placeholder, disabled, ...resto }: {
  valor: number | null; onCambio: (c: number | null) => void; id?: string; placeholder?: string; disabled?: boolean; 'aria-label'?: string;
}) {
  const [texto, setTexto] = useState(textoDesdeCentavos(valor));
  const ultimo = useRef(valor);
  useEffect(() => {
    if (valor !== ultimo.current) { setTexto(textoDesdeCentavos(valor)); ultimo.current = valor; }
  }, [valor]);
  return (
    <span className="pesos">
      <input id={id} inputMode="decimal" value={texto} placeholder={placeholder} disabled={disabled} aria-label={resto['aria-label']}
        onChange={(e) => { setTexto(e.target.value); const c = centavosDesdeTexto(e.target.value); ultimo.current = c; onCambio(c); }} />
    </span>
  );
}

export function EntradaNumero({ valor, onCambio, sufijo, paso = 1, min, max, id, ...resto }: {
  valor: number; onCambio: (n: number) => void; sufijo?: string; paso?: number; min?: number; max?: number; id?: string; 'aria-label'?: string;
}) {
  const [texto, setTexto] = useState(String(valor));
  useEffect(() => { if (Number(texto) !== valor) setTexto(String(valor)); }, [valor]); // eslint-disable-line react-hooks/exhaustive-deps
  const input = (
    <input id={id} type="number" inputMode="decimal" step={paso} min={min} max={max} value={texto} aria-label={resto['aria-label']}
      onChange={(e) => { setTexto(e.target.value); const n = Number(e.target.value); if (e.target.value !== '' && Number.isFinite(n)) onCambio(n); }} />
  );
  return sufijo ? <span className="sufijo">{input}<span>{sufijo}</span></span> : input;
}

export function Seccion({ titulo, resumen, abierta, children }: { titulo: string; resumen?: ReactNode; abierta?: boolean; children: ReactNode }) {
  return (
    <details className="secc" open={abierta}>
      <summary><span className="h3">{titulo}</span>{resumen != null && <span className="res">{resumen}</span>}</summary>
      <div className="cuerpo">{children}</div>
    </details>
  );
}

export function Dialogo({ abierto, onCerrar, titulo, children, ancho, pie }: {
  abierto: boolean; onCerrar: () => void; titulo: ReactNode; children: ReactNode; ancho?: boolean; pie?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const idTitulo = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierto && !d.open) { if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', ''); }
    if (!abierto && d.open) { if (typeof d.close === 'function') d.close(); else d.removeAttribute('open'); }
  }, [abierto]);
  return (
    <dialog ref={ref} className={ancho ? 'ancho' : undefined} aria-labelledby={idTitulo}
      onCancel={(e) => { e.preventDefault(); onCerrar(); }}
      onClick={(e) => { if (e.target === ref.current) onCerrar(); }}>
      {abierto && (
        <div className="cuerpo-dlg">
          <h2 id={idTitulo}>{titulo}</h2>
          {children}
          {pie && <div className="acciones">{pie}</div>}
        </div>
      )}
    </dialog>
  );
}

export function Confirmar({ abierto, titulo, children, textoConfirmar = 'Confirmar', onConfirmar, onCancelar, ocupado, tono = 'primario' }: {
  abierto: boolean; titulo: string; children: ReactNode; textoConfirmar?: string; onConfirmar: () => void; onCancelar: () => void;
  ocupado?: boolean; tono?: 'primario' | 'naranja' | 'peligro' | 'marca';
}) {
  return (
    <Dialogo abierto={abierto} onCerrar={onCancelar} titulo={titulo}
      pie={<>
        <button type="button" className="btn" onClick={onCancelar} disabled={ocupado}>Cancelar</button>
        <button type="button" className={`btn btn--${tono}`} onClick={onConfirmar} disabled={ocupado} autoFocus>{ocupado ? 'Un momento…' : textoConfirmar}</button>
      </>}>
      {children}
    </Dialogo>
  );
}

// ───── Avisos flotantes ─────

type TipoAviso = 'ok' | 'bad' | 'warn' | 'info';
interface AvisoFlotante { id: number; texto: string; tipo: TipoAviso }
const CtxAvisos = createContext<(texto: string, tipo?: TipoAviso) => void>(() => {});

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [lista, setLista] = useState<AvisoFlotante[]>([]);
  const avisar = useCallback((texto: string, tipo: TipoAviso = 'info') => {
    const id = Date.now() + Math.random();
    setLista((l) => [...l.slice(-2), { id, texto, tipo }]);
    setTimeout(() => setLista((l) => l.filter((a) => a.id !== id)), tipo === 'bad' ? 7000 : 3500);
  }, []);
  return (
    <CtxAvisos.Provider value={avisar}>
      {children}
      <div className="toasts" aria-live="polite">
        {lista.map((a) => <div key={a.id} className={`aviso${a.tipo === 'info' || a.tipo === 'ok' ? '' : ` aviso--${a.tipo}`}`}>{a.texto}</div>)}
      </div>
    </CtxAvisos.Provider>
  );
}

export const useAvisar = () => useContext(CtxAvisos);

export const mensajeError = (e: unknown) => (e instanceof Error ? e.message : 'Algo salió mal');
